import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	EuiButtonIcon,
	EuiContextMenuItem,
	EuiContextMenuPanel,
	EuiPopover,
} from '@equipmentshare/ds2'
import { heapEvent } from '@equipmentshare/heaper'
import { Permission, TransferOrderStatus, TransferOrderType } from '../../../graphql'
import usePermissions from '../../../hooks/usePermissions'
import { checkPermissions } from '../../../components/PermissionsCheck/helpers'
import { TransferOrderRow } from '../hooks/useTransferOrdersDataQuery'
import CancelTransferOrderModal from './CancelTransferOrderModal'
import { cancelRestrictedTooltip } from '../../../constants/transfer-cancellation'
import { CANCELLABLE_STATUSES } from '../constants'
import ApproveRejectTransferOrderModal from './ApproveRejectTransferOrderModal'

type TransferOrderActionsMenuProps = {
	row: TransferOrderRow,
}

export default function TransferOrderActionsMenu( { row }: TransferOrderActionsMenuProps ) {
	const navigate = useNavigate()
	const [ isPopoverOpen, setIsPopoverOpen ] = useState( false )
	const [ showCancelModal, setShowCancelModal ] = useState( false )
	const [ showApproveModal, setShowApproveModal ] = useState( false )
	const [ showRejectModal, setShowRejectModal ] = useState( false )

	const { userPermissions } = usePermissions( [
		Permission.CanCompletePartsTransferByStoreId,
		Permission.CanApprovePartsTransferByStoreId,
	] )
	const canReceive = checkPermissions( userPermissions, [ Permission.CanCompletePartsTransferByStoreId ] )
	const canApprove = checkPermissions( userPermissions, [ Permission.CanApprovePartsTransferByStoreId ] )
	// Scoped to from_store and resolved once for the whole page by
	// useTransferOrdersDataQuery — asking here would be one request per row.
	const { canCancel, blockedAsReceiver } = row.cancelPermission

	const isReceivable = row.status === TransferOrderStatus.Approved || row.status === TransferOrderStatus.InTransit
	const isCancellable = CANCELLABLE_STATUSES.has( row.status )
	const isApprovable = row.transferOrderType === TransferOrderType.Manual && row.status === TransferOrderStatus.Pending

	const togglePopover = () => setIsPopoverOpen( ( prev ) => !prev )
	const closePopover = () => setIsPopoverOpen( false )

	const handleViewDetails = ( e: React.MouseEvent ) => {
		e.preventDefault()
		closePopover()
		heapEvent( 'Inventory - Transfer Orders - List - Click - View Details', { transfer_order_id: row.id } )
		navigate( `/transfer-orders/${row.id}` )
	}

	const handleReceiveItems = ( e: React.MouseEvent ) => {
		e.preventDefault()
		closePopover()
		heapEvent( 'Inventory - Transfer Orders - List - Click - Receive Items', { transfer_order_id: row.id } )
		navigate( `/transfer-orders/${row.id}/receive` )
	}

	const handleCancelClick = () => {
		closePopover()
		heapEvent( 'Inventory - Transfer Orders - List - Click - Cancel', { transfer_order_id: row.id } )
		setShowCancelModal( true )
	}

	const handleApproveClick = () => {
		closePopover()
		heapEvent( 'Inventory - Transfer Orders - List - Click - Approve', { transfer_order_id: row.id } )
		setShowApproveModal( true )
	}

	const handleRejectClick = () => {
		closePopover()
		heapEvent( 'Inventory - Transfer Orders - List - Click - Reject', { transfer_order_id: row.id } )
		setShowRejectModal( true )
	}

	const handleApproveSuccess = () => {
		setShowApproveModal( false )
		navigate( `/transfer-orders/${row.id}` )
	}

	const handleRejectSuccess = () => {
		setShowRejectModal( false )
		navigate( `/transfer-orders/${row.id}` )
	}

	const menuItems = [
		<EuiContextMenuItem key="view-details" icon="eye" href={`/transfer-orders/${row.id}`} onClick={handleViewDetails}>
			View Details
		</EuiContextMenuItem>,
	]

	if ( isReceivable && canReceive ) {
		menuItems.push(
			<EuiContextMenuItem key="receive-items" icon="package" href={`/transfer-orders/${row.id}/receive`} onClick={handleReceiveItems}>
				Receive Items
			</EuiContextMenuItem>
		)
	}

	if ( isApprovable && canApprove ) {
		menuItems.push(
			<EuiContextMenuItem key="approve" icon="check" onClick={handleApproveClick}>
				Approve
			</EuiContextMenuItem>
		)
		menuItems.push(
			<EuiContextMenuItem key="reject" icon="cross" onClick={handleRejectClick}>
				Reject
			</EuiContextMenuItem>
		)
	}

	if ( isCancellable && ( canCancel || blockedAsReceiver ) ) {
		// Receiving-side viewers get the action greyed out with an explanation rather
		// than a missing menu item, so the restriction reads as deliberate.
		menuItems.push(
			<EuiContextMenuItem
				disabled={!canCancel}
				icon="cross"
				key="cancel"
				onClick={handleCancelClick}
				toolTipContent={canCancel ? undefined : cancelRestrictedTooltip( !!row.fromStore?.isWarehouse )}
			>
				Cancel Order
			</EuiContextMenuItem>
		)
	}

	return (
		<>
			<EuiPopover
				button={
					<EuiButtonIcon
						iconType="boxesHorizontal"
						aria-label="Actions"
						color="text"
						onClick={togglePopover}
					/>
				}
				isOpen={isPopoverOpen}
				closePopover={() => setIsPopoverOpen( false )}
				panelPaddingSize="none"
				anchorPosition="downLeft"
			>
				<EuiContextMenuPanel items={menuItems} />
			</EuiPopover>

			{showCancelModal && (
				<CancelTransferOrderModal
					transferOrderId={row.id}
					onClose={() => setShowCancelModal( false )}
					onSuccess={() => setShowCancelModal( false )}
				/>
			)}
			{showApproveModal && (
				<ApproveRejectTransferOrderModal
					transferOrderId={row.id}
					action="approve"
					onClose={() => setShowApproveModal( false )}
					onSuccess={handleApproveSuccess}
				/>
			)}
			{showRejectModal && (
				<ApproveRejectTransferOrderModal
					transferOrderId={row.id}
					action="reject"
					onClose={() => setShowRejectModal( false )}
					onSuccess={handleRejectSuccess}
				/>
			)}
		</>
	)
}
